import { useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Info from "@mui/icons-material/Info";
import {
  Box,
  Checkbox,
  Collapse,
  FormControlLabel,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { NhsNumberSchema } from "@thalamos/common";

import { useScreenDetection } from "../../../hooks/ScreenDetection/useScreenDetection.js";
import { HorizontalLine } from "../../form/HorizontalLine.js";
import { PatientCrisisInfo } from "../../layout/PatientCrisisInfo.js";
import { Container, ExpandMore } from "../../layout/styleWrappers.js";
import { Button } from "~/components/design-system/Button/Button.js";
import { TooltipInfoBox } from "~/components/design-system/TooltipInfoBox.js";

export interface PatientCardExtendedProps {
  patientName: string;
  patientDateOfBirth?: string | null;
  address?: string;
  isSelected: boolean;
  setIsSelected: () => void;
  selectionComponent?: "checkbox" | "button";
  patientAge: string;
  nhsNumber?: string | null;
  isDeceased?: boolean;
}

export const PatientCardExtended = (props: PatientCardExtendedProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const {
    patientName,
    nhsNumber,
    patientDateOfBirth,
    address,
    isSelected,
    setIsSelected,
    selectionComponent,
    patientAge,
  } = props;
  const [expanded, setExpanded] = useState(false);

  const { isMobileView } = useScreenDetection();

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const styles = {
    subtitle: {
      fontSize: theme.spacing(1.4),
      color: theme.palette.primary.hint,
    },
    border: {
      borderRight: `1px solid ${theme.palette.background.horizontalGray}`,
      paddingRight: theme.spacing(1.5),
      marginRight: theme.spacing(1.5),
    },
  };

  return (
    <Container>
      <Box
        sx={{
          padding: theme.spacing(3),
          border: `1px solid ${theme.palette.common.royalBlue}`,
          borderRadius: "6px 6px 0 0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography sx={styles.subtitle}>
              {t("components.cardExtended.patientName")}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography
                sx={{
                  fontSize: theme.spacing(2.25),
                  fontWeight: 600,
                }}
              >
                {patientName}
              </Typography>
              {props.isDeceased && (
                <TooltipInfoBox
                  title="This patient has been marked as deceased in NHS national systems"
                  icon={<Info sx={{ fontSize: "1.5rem", ml: 1 }} />}
                />
              )}
            </Box>
          </Box>
          {selectionComponent === "button" ? (
            <Button
              variant={"contained"}
              color="primary"
              onClick={setIsSelected}
              label={t("components.cardExtended.selectPatientLabel")}
            />
          ) : (
            <FormControlLabel
              sx={{ mx: 0 }}
              control={
                <Checkbox
                  name="selectPatient"
                  checked={isSelected}
                  sx={{ color: "primary.main" }}
                />
              }
              label={t("components.cardExtended.selectPatientLabel")}
              onChange={setIsSelected}
            />
          )}
        </Box>

        <HorizontalLine topMargin />
        <Box
          sx={{
            position: "relative",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={[
                {
                  display: "flex",
                  flexDirection: "column",
                },
                styles.border,
              ]}
            >
              <Typography sx={styles.subtitle}>
                {t("components.cardExtended.patientDateOfBirth")}
              </Typography>
              <Typography>{patientDateOfBirth}</Typography>
            </Box>
            {nhsNumber && (
              <Box
                sx={[
                  {
                    display: "flex",
                    flexDirection: "column",
                  },
                  styles.border,
                ]}
              >
                <Typography sx={styles.subtitle}>NHS Number</Typography>
                <Typography>
                  {NhsNumberSchema.catch(t("common.unknown")).parse(nhsNumber)}
                </Typography>
              </Box>
            )}
          </Box>

          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <HorizontalLine topMargin />
            <Box
              sx={{
                display: "flex",
                mt: 2,
                flexWrap: { sm: "wrap", md: "nowrap" },
              }}
            >
              <Box
                sx={[
                  {
                    display: "flex",
                    flexDirection: "column",
                  },
                  isMobileView ? {} : styles.border,
                ]}
              >
                <Typography sx={styles.subtitle}>
                  {t("components.cardExtended.age")}
                </Typography>
                <Typography>
                  {patientAge ? patientAge : t("common.unknown")}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  mt: { sm: 1, md: 0 },
                }}
              >
                <Typography sx={styles.subtitle}>
                  {t("components.cardExtended.address")}
                </Typography>
                <Typography>{address}</Typography>
              </Box>
            </Box>
          </Collapse>

          <Box
            component={ExpandMore}
            sx={{
              display: "flex",
              position: "absolute",
              right: 0,
              bottom: 0,
              [theme.breakpoints.down("sm")]: {
                left: "100%",
              },
            }}
            role="button"
            data-testid="expand-more"
            isExpanded={expanded}
            onClick={handleExpandClick}
            aria-expanded={expanded}
            aria-label={t("components.cardExtended.expandMoreLabel")}
          >
            <ExpandMoreIcon sx={{ color: "primary.main" }} />
          </Box>
        </Box>
      </Box>
      <PatientCrisisInfo />
    </Container>
  );
};
